import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Pencil, Linkedin, MapPin, Briefcase, Calendar, Mail, GraduationCap, Home, Cake, UserCog, UserPlus } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { initials, photoOf, tenureLabel, fmtDate, bandColor } from "@/lib/orgUtils";

const Item = ({ icon: Icon, label, value, testid }) => value ? (
  <div className="flex items-start gap-3" data-testid={testid}>
    <Icon size={15} className="text-[#9699AD] mt-0.5 shrink-0" />
    <div className="min-w-0">
      <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#9699AD]">{label}</p>
      <p className="text-sm font-semibold break-words">{value}</p>
    </div>
  </div>
) : null;

export default function ProfilePanel({ emp, employees, view, onClose, onChanged, canEdit, canReparent, onAddReport, onEditPerson }) {
  const { user } = useAuth();
  const [editMgr, setEditMgr] = useState(false);
  const [newMgr, setNewMgr] = useState("");
  useEffect(() => { setEditMgr(false); }, [emp?.id]);

  if (!emp) return null;
  const isSelf = user?.linked_employee_id === emp.id;
  const manager = employees.find((e) => e.employee_id === emp.manager_employee_id);
  const reports = employees.filter((e) => e.manager_employee_id === emp.employee_id && e.status === "active");
  const color = bandColor(emp.date_of_joining, view?.color_config);

  const changeManager = async () => {
    try {
      await api.put(`/employees/${emp.id}/manager`, { manager_employee_id: newMgr || null });
      toast.success(`${emp.full_name} moved successfully`);
      setEditMgr(false);
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <AnimatePresence>
      <motion.aside
        key={emp.id}
        initial={{ x: 420 }} animate={{ x: 0 }} exit={{ x: 420 }}
        transition={{ type: "spring", damping: 28, stiffness: 300 }}
        className="absolute top-0 right-0 h-full w-[380px] bg-white border-l border-[#181B34]/10 shadow-[-16px_0_48px_rgba(24,27,52,0.1)] z-40 flex flex-col"
        data-testid="profile-panel"
      >
        <div className="h-2" style={{ background: color || "#6161FF" }} />
        <div className="p-6 flex items-start gap-4">
          {photoOf(emp) ? (
            <img src={photoOf(emp)} alt="" className="w-16 h-16 rounded-2xl object-cover shadow" />
          ) : (
            <span className="w-16 h-16 rounded-2xl bg-[#F0F3FF] text-[#6161FF] flex items-center justify-center text-xl font-black">{initials(emp.full_name)}</span>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-black tracking-tight leading-tight">{emp.full_name}</h3>
            <p className="text-sm font-semibold text-[#676a82]">{emp.designation}</p>
            <p className="text-xs font-mono text-[#9699AD] mt-0.5">{emp.employee_id}</p>
          </div>
          <button data-testid="close-profile-panel" onClick={onClose} className="w-8 h-8 rounded-full hover:bg-[#F0F3FF] flex items-center justify-center text-[#676a82]"><X size={17} /></button>
        </div>
        <div className="flex-1 overflow-auto px-6 pb-6 space-y-4">
          <div className="grid grid-cols-1 gap-3.5 p-4 rounded-xl bg-[#F6F7FB]">
            <Item icon={Briefcase} label="Department" value={emp.department} />
            <Item icon={Calendar} label="Date of Joining" value={emp.date_of_joining ? `${fmtDate(emp.date_of_joining)} · ${tenureLabel(emp.date_of_joining)}` : null} />
            <Item icon={Mail} label="Email" value={emp.email} />
            <Item icon={MapPin} label="Location" value={emp.location} />
            <Item icon={Briefcase} label="Employment Type" value={emp.employment_type} />
            <Item icon={GraduationCap} label="Highest Qualification" value={emp.highest_qualification} />
            <Item icon={Cake} label="Date of Birth" value={emp.date_of_birth ? fmtDate(emp.date_of_birth) : null} />
            <Item icon={Home} label="Address" value={emp.address} />
            {emp.linkedin_url && (
              <a href={emp.linkedin_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm font-bold text-[#579BFC] hover:underline">
                <Linkedin size={15} /> LinkedIn profile
              </a>
            )}
            {Object.entries(emp.custom_fields || {}).map(([k, v]) => (
              <Item key={k} icon={Briefcase} label={k} value={v} />
            ))}
          </div>

          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#9699AD] mb-1.5">Reports to</p>
            {editMgr ? (
              <div className="flex gap-2">
                <select data-testid="change-manager-select" value={newMgr} onChange={(e) => setNewMgr(e.target.value)}
                  className="flex-1 h-9 px-2 rounded-lg border border-[#181B34]/15 text-sm font-semibold bg-white">
                  <option value="">— No manager (root) —</option>
                  {employees.filter((e) => e.id !== emp.id && e.status === "active").map((e) => (
                    <option key={e.id} value={e.employee_id}>{e.full_name} — {e.designation}</option>
                  ))}
                </select>
                <button data-testid="confirm-change-manager" onClick={changeManager} className="px-3 h-9 rounded-lg bg-[#6161FF] text-white text-sm font-bold">Save</button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold">{manager ? `${manager.full_name} · ${manager.designation}` : "— (root)"}</p>
                {canReparent && (
                  <button data-testid="change-manager-btn" onClick={() => { setEditMgr(true); setNewMgr(emp.manager_employee_id || ""); }}
                    className="flex items-center gap-1 text-xs font-bold text-[#6161FF] hover:underline"><UserCog size={13} /> Change</button>
                )}
              </div>
            )}
          </div>

          {reports.length > 0 && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#9699AD] mb-1.5">Direct reports ({reports.length})</p>
              <div className="space-y-1">
                {reports.map((r) => (
                  <p key={r.id} className="text-sm font-semibold text-[#4b4e63]">{r.full_name} <span className="text-xs text-[#9699AD]">· {r.designation}</span></p>
                ))}
              </div>
            </div>
          )}
        </div>
        {(isSelf || canEdit) && (
          <div className="p-4 border-t border-[#181B34]/10 flex gap-2">
            {isSelf && (
              <a href="/profile" data-testid="edit-profile-link"
                className="flex-1 h-10 rounded-xl flex items-center justify-center gap-2 text-sm font-bold bg-[#6161FF] text-white">
                <Pencil size={14} /> Edit my profile
              </a>
            )}
            {canEdit && (
              <>
                <button data-testid="edit-person-btn" onClick={() => onEditPerson?.(emp)}
                  className="flex-1 h-10 rounded-xl flex items-center justify-center gap-2 text-sm font-bold bg-[#F0F3FF] text-[#6161FF] hover:bg-[#6161FF]/15">
                  <Pencil size={14} /> Edit person
                </button>
                <button data-testid="add-report-btn" onClick={() => onAddReport?.(emp)}
                  className="flex-1 h-10 rounded-xl flex items-center justify-center gap-2 text-sm font-bold bg-[#00C875]/12 text-[#00925a] hover:bg-[#00C875]/25">
                  <UserPlus size={14} /> Add report
                </button>
              </>
            )}
          </div>
        )}
      </motion.aside>
    </AnimatePresence>
  );
}
